import React, { useEffect, useState } from "react";
import { Plug, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  listIntegrations,
  connectIntegration,
  resyncIntegration,
  configureIntegration,
} from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [configTarget, setConfigTarget] = useState(null); // integration being configured
  const [scopeInput, setScopeInput] = useState("");
  const [coverageInput, setCoverageInput] = useState("");

  useEffect(() => {
    listIntegrations()
      .then((r) => setItems(r.items || []))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load integrations"));
  }, []);

  function updateItem(id, updated) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updated } : i)));
  }

  async function handleConnect(integration) {
    try {
      const res = await connectIntegration(integration.id);
      updateItem(integration.id, res.integration);
      toast.success(`${integration.name} connected`);
    } catch {
      toast.error("Connection failed");
    }
  }

  async function handleResync(integration) {
    try {
      const res = await resyncIntegration(integration.id);
      updateItem(integration.id, res.integration);
      toast.success(`${integration.name} synced`);
    } catch {
      toast.error("Sync failed");
    }
  }

  function openConfigure(integration) {
    setConfigTarget(integration);
    setScopeInput(integration.scope || "");
    setCoverageInput(integration.policy_coverage || "");
  }

  async function handleConfigure() {
    if (!configTarget) return;
    try {
      const res = await configureIntegration(configTarget.id, {
        scope: scopeInput || undefined,
        policy_coverage: coverageInput || undefined,
      });
      updateItem(configTarget.id, res.integration);
      toast.success(`${configTarget.name} updated`);
      setConfigTarget(null);
    } catch {
      toast.error("Configuration failed");
    }
  }

  const categories = Array.from(new Set(items.map((i) => i.category)));

  return (
    <div className="p-8 md:p-10 space-y-8" data-testid="integrations-page">
      <Dialog open={!!configTarget} onOpenChange={(open) => { if (!open) setConfigTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure {configTarget?.name}</DialogTitle>
            <DialogDescription>Update scope and policy coverage for this integration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1.5">Scope</label>
              <input
                value={scopeInput}
                onChange={(e) => setScopeInput(e.target.value)}
                placeholder="e.g. Tickets, macros, KB"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Policy coverage</label>
              <input
                value={coverageInput}
                onChange={(e) => setCoverageInput(e.target.value)}
                placeholder="e.g. Full, Partial, Scoped"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfigTarget(null)}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleConfigure}
                className="rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
              >
                Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div>
        <div className="tracked-caps">Scope</div>
        <h2 className="mt-1 font-serif-display text-3xl">Integrations</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          ProofMark governs knowledge objects and publication workflow first. Runtime observation is scoped and optional.
        </p>
      </div>

      {categories.map((cat) => (
        <section key={cat} className="space-y-3">
          <div className="tracked-caps">{cat}</div>
          <div className="grid md:grid-cols-3 gap-4">
            {items.filter((i) => i.category === cat).map((i) => (
              <article key={i.id} className="rounded-lg border border-border bg-card p-5" data-testid={`integration-${i.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded border border-border grid place-items-center bg-muted/30">
                      <Plug className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <h3 className="font-serif-display text-lg">{i.name}</h3>
                  </div>
                  <StatusBadge value={i.status} />
                </div>
                <dl className="mt-5 space-y-2 text-sm">
                  <Row k="Health"><StatusBadge value={i.health} /></Row>
                  <Row k="Scope">{i.scope}</Row>
                  <Row k="Policy coverage">{i.policy_coverage}</Row>
                  <Row k="Last sync"><span className="font-mono text-xs">{i.last_sync ? `${fmtDate(i.last_sync)} · ${fmtTime(i.last_sync)}` : "—"}</span></Row>
                </dl>

                {i.warnings?.length > 0 && (
                  <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{i.warnings[0]}</span>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2">
                  {i.status === "connected" ? (
                    <>
                      <button
                        onClick={() => openConfigure(i)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted"
                      >
                        Configure
                      </button>
                      <button
                        onClick={() => handleResync(i)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted"
                      >
                        Re-sync
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleConnect(i)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-3 py-1.5 text-xs hover:opacity-90"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Connect
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Row({ k, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="tracked-caps">{k}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
